import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Recent items storage utilities
const RECENT_STORAGE_PREFIX = 'recent_';
const MAX_RECENT_ITEMS = 50;

const getRecentItems = (section) => {
  try {
    const key = `${RECENT_STORAGE_PREFIX}${section}`;
    const items = JSON.parse(localStorage.getItem(key) || '[]');
    return items.slice(0, 5); // Show only 5 most recent
  } catch (error) {
    console.warn(`Failed to load recent items for ${section}:`, error);
    return [];
  }
};

const addToRecentItems = (section, item) => {
  try {
    const key = `${RECENT_STORAGE_PREFIX}${section}`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    
    // Remove if already exists (to move to front)
    const filtered = existing.filter(existingItem => existingItem.id !== item.id);
    
    // Add to front and cap at MAX_RECENT_ITEMS
    const updated = [item, ...filtered].slice(0, MAX_RECENT_ITEMS);
    
    localStorage.setItem(key, JSON.stringify(updated));
  } catch (error) {
    console.warn(`Failed to save recent item for ${section}:`, error);
  }
};

const getSectionName = (pathname) => {
  const section = pathname.split('/')[1] || 'dashboard';
  const sectionNames = {
    'dashboard': 'Notes',
    'bookmarks': 'Bookmarks',
    'voices': 'Voices',
    'sessions': 'Sessions',
    'reflections': 'Reflections',
    'imports': 'Imports'
  };
  return sectionNames[section] || 'Items';
};

const getCurrentSection = (pathname) => {
  return pathname.split('/')[1] || 'dashboard';
};

export const useCommandBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [FuseConstructor, setFuseConstructor] = useState(null);
  
  const currentSection = getCurrentSection(location.pathname);
  const sectionName = getSectionName(location.pathname);

  // Dynamically import Fuse.js (as per GPT5 guardrails)
  useEffect(() => {
    let mounted = true;
    
    const loadFuse = async () => {
      try {
        const FuseModule = await import('fuse.js');
        if (mounted) {
          // Store a function that creates new Fuse instances
          setFuseConstructor(() => FuseModule.default);
        }
      } catch (error) {
        console.warn('Failed to load Fuse.js, falling back to simple search:', error);
        if (mounted) {
          setFuseConstructor(null); // Will use simple includes fallback
        }
      }
    };

    loadFuse();
    return () => { mounted = false; };
  }, []);

  // Mock projects data - in real app this would come from context/state
  const mockProjects = useMemo(() => [
    { id: '1', name: 'Work Research', description: 'Work-related bookmarks and notes', icon: '💼' },
    { id: '2', name: 'Personal Blog', description: 'Blog post ideas and drafts', icon: '✍️' },
    { id: '3', name: 'Learning React', description: 'React tutorials and examples', icon: '⚛️' },
    { id: '4', name: 'Side Project', description: 'Weekend coding project', icon: '🚀' },
    { id: '5', name: 'Home Improvement', description: 'DIY guides and tools', icon: '🔨' }
  ], []);

  const recentItems = useMemo(() => 
    getRecentItems(currentSection), 
    [currentSection]
  );

  const quickActions = useMemo(() => [
    { 
      id: 'new-note', 
      label: 'New Note', 
      shortcut: '⌘N', 
      icon: '📝',
      action: () => navigate('/dashboard')
    },
    { 
      id: 'new-bookmark', 
      label: 'New Bookmark', 
      shortcut: '⌘B', 
      icon: '🔖',
      action: () => {
        // Trigger bookmark dialog - would need to access parent state
        window.dispatchEvent(new CustomEvent('cmdbar.new_bookmark'));
      }
    },
    { 
      id: 'toggle-density', 
      label: 'Toggle Density', 
      shortcut: '', 
      icon: '🔧',
      action: () => {
        window.dispatchEvent(new CustomEvent('cmdbar.toggle_density'));
      }
    },
    { 
      id: 'go-insights', 
      label: 'Go to Insights', 
      shortcut: '', 
      icon: '💡',
      action: () => navigate('/insights')
    }
  ], [navigate]);

  // Fuzzy search for projects with fallback
  const filterProjects = useCallback((projects, query) => {
    if (!query) return projects.slice(0, 5);
    
    if (FuseConstructor) {
      // Use Fuse.js for fuzzy search
      const fuse = new FuseConstructor(projects, {
        keys: ['name', 'description'],
        threshold: 0.4,
        includeScore: true
      });
      return fuse.search(query).map(result => result.item).slice(0, 5);
    } else {
      // Fallback to simple includes
      return projects.filter(project =>
        project.name.toLowerCase().includes(query.toLowerCase()) ||
        (project.description && project.description.toLowerCase().includes(query.toLowerCase()))
      ).slice(0, 5);
    }
  }, [FuseConstructor]);

  const filterActions = useCallback((actions, query) => {
    if (!query) return actions;
    
    return actions.filter(action =>
      action.label.toLowerCase().includes(query.toLowerCase())
    );
  }, []);

  // Telemetry functions
  const trackEvent = useCallback((eventName, data = {}) => {
    // Add current section to all events
    const eventData = { ...data, section: currentSection };
    
    // Send to analytics (placeholder)
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[CommandBar] ${eventName}:`, eventData);
    }
    
    // In production, would send to analytics service
    // if (window.gtag) {
    //   window.gtag('event', eventName, eventData);
    // }
  }, [currentSection]);

  const handleProjectSwitch = useCallback((project) => {
    trackEvent('cmdbar.project_switch', {
      projectId: project.id,
      projectName: project.name,
      fromSection: currentSection
    });

    // Add to recent projects
    addToRecentItems('projects', {
      id: project.id,
      name: project.name,
      description: project.description,
      icon: project.icon,
      timestamp: new Date().toISOString()
    });

    // Navigate or filter current page by project
    // Implementation would depend on current page's state management
    window.dispatchEvent(new CustomEvent('cmdbar.project_switch', {
      detail: { projectId: project.id, projectName: project.name }
    }));
  }, [currentSection, trackEvent]);

  const handleSearch = useCallback((query) => {
    if (!query.trim()) return;
    
    trackEvent('cmdbar.query', { 
      qlen: query.length // Privacy by design - don't send raw query
    });

    // Forward search to current page
    window.dispatchEvent(new CustomEvent('cmdbar.search', {
      detail: { section: currentSection, query: query.trim() }
    }));

    trackEvent('cmdbar.search_forwarded', { section: currentSection });
  }, [currentSection, trackEvent]);

  const handleQuickAction = useCallback((action) => {
    trackEvent('cmdbar.quick_action', {
      actionId: action.id,
      fromSection: currentSection
    });

    if (action.action) {
      action.action();
    }
  }, [currentSection, trackEvent]);

  return {
    currentSection,
    sectionName,
    recentProjects: mockProjects, // In real app, would come from context
    recentItems,
    quickActions,
    filterProjects,
    filterActions,
    handleProjectSwitch,
    handleSearch,
    handleQuickAction,
    trackEvent
  };
};